import { StateCreator } from "zustand"
import { StoreType } from "./AppStore"
import { autoAceiteLigado } from "./ConfigsStore"
import { TabStatusEnum } from "./TabStatusStore"

export type AvisoPedidoStore = {
    getAvisoElement: () => HTMLAudioElement | null
    // true quando o navegador bloqueou o play() por autoplay: o Cabecalho mostra um
    // aviso para o usuario liberar o som com um clique (gesto exigido pelo navegador).
    somBloqueado: boolean
    ativarAviso: () => void
    desativarAviso: () => void
    toggleAviso: () => void
    // Chamado DENTRO de um clique do usuario (o gesto que o navegador exige): toca o
    // aviso pendente e destrava o autoplay para os proximos toques da sessao.
    autorizarSom: () => void
}

// Lê a DURAÇÃO do alerta sonoro direto do localStorage persistido (zustand "app-store"),
// que é a fonte única entre todas as abas. Sem isso, cada aba usa a cópia EM MEMÓRIA do
// config, hidratada no load: mudar a duração numa aba escreve no localStorage mas NÃO
// atualiza as demais até um reload — por isso "dependia de configurar em cada aba". Aqui,
// quem for tocar lê sempre o último valor configurado em qualquer aba.
// Retorna a string da duração (segundos) ou undefined se não houver/for ilegível.
export function lerDuracaoEfeitoSonoroPersistida(): string | undefined {
    try {
        const cru = localStorage.getItem("app-store")
        if (!cru) return undefined
        const parsed = JSON.parse(cru)
        // zustand persist grava como { state: {...}, version }. Defensivo caso mude.
        const configuracoes = parsed?.state?.configuracoes ?? parsed?.configuracoes
        const duracao = configuracoes?.recebedor?.duracaoEfeitoSonoro
        return duracao == null || duracao === "" ? undefined : String(duracao)
    } catch {
        return undefined
    }
}

const createAvisoPedidoStore: StateCreator<StoreType, [], [], AvisoPedidoStore> = (set, get) => ({
    getAvisoElement: () => document.getElementById("audiopedido") as HTMLAudioElement,
    somBloqueado: false,
    ativarAviso: () => {
        // Só a estação em OPERAÇÃO (CRUD) toca o som de novo pedido. Visualização
        // (acompanha) e Garçom (só lançamento) ficam MUDAS: um aparelho secundário —
        // ex.: o celular do garçom em Visualização — não toca, enquanto a estação de
        // recebimento (o computador, em Operação) continua tocando normal. O modo é POR
        // APARELHO (tabStatus é persistido no localStorage de cada navegador). Trava aqui
        // (e não em cada chamador) para cobrir toda origem de som: evento leve, evento
        // pesado de novo pedido e a re-checagem de pendentes.
        if (get().tabStatus !== TabStatusEnum.CRUD) return

        if (!get().configuracoes!.recebedor.efeitoSonoro) return

        // O navegador bloqueia play() programatico antes de o usuario interagir com a
        // pagina (politica de autoplay). O desbloqueio acontece no primeiro gesto (ver
        // Cabecalho). Se ainda assim o play falhar (autoplay bloqueado, elemento
        // ausente), logamos — sem esse aviso, "o pedido chega mas nao toca" fica
        // invisivel no console e impossivel de diagnosticar.
        const elemento = get().getAvisoElement()
        if (!elemento) {
            console.warn("[AvisoPedido] elemento #audiopedido nao encontrado — som nao toca")
            return
        }
        elemento
            .play?.()
            ?.then(() => {
                // Tocou: se havia aviso de bloqueio na tela, ja pode sumir.
                if (get().somBloqueado) set({ somBloqueado: false })
            })
            ?.catch(err => {
                // Autoplay bloqueado (usuario ainda nao interagiu com a pagina): liga o
                // aviso para ele liberar o som com um clique.
                console.warn("[AvisoPedido] play() bloqueado/falhou (autoplay?):", err?.name || err)
                set({ somBloqueado: true })
            })

        // Quanto tempo o som toca, por modo:
        //
        //  - `duracaoEfeitoSonoro` definido (qualquer modo automático): toca por esse
        //    tempo e para.
        //  - Vazio + PREPARO automático: o pedido vai DIRETO para "Em preparação" (não
        //    descansa em "Aceito"), então não há transição manual para parar o som —
        //    usa o fallback fixo de 10s para não tocar indefinidamente.
        //  - Vazio + aceite automático SEM preparo automático: NÃO agenda parada — toca
        //    até o pedido sair de "Aceito" para "Em preparação". Quem para é o Recebedor,
        //    que chama `desativarAviso` quando a lista de Aceitos esvazia (ou a bandeja).
        //  - Nenhum modo automático (manual): sem timeout — toca até `desativarAviso`
        //    (clique na bandeja ou aceite do pedido, que esvazia a lista de Aguardando).
        const recebedor = get().configuracoes!.recebedor
        // Preparo automático só conta aqui com o aceite automático: com aceite MANUAL o
        // som chama até o operador aceitar, igual à operação manual.
        const autoPreparo = !!recebedor.prepararPedidoAutomatico && autoAceiteLigado(recebedor)
        // Duração SINCRONIZADA entre abas: lê do localStorage (fonte única), com fallback
        // para o valor em memória desta aba caso o localStorage não tenha (ex.: primeiro
        // load antes de persistir).
        const duracaoEfeitoSonoro =
            lerDuracaoEfeitoSonoroPersistida() ?? recebedor.duracaoEfeitoSonoro
        if (autoAceiteLigado(recebedor) || autoPreparo) {
            if (duracaoEfeitoSonoro)
                setTimeout(get().desativarAviso, Number(duracaoEfeitoSonoro) * 1000)
            else if (autoPreparo) setTimeout(get().desativarAviso, 10000)
            // else (aceite automático sem preparo + duração vazia): sem timeout.
        }
    },
    desativarAviso: () => get().getAvisoElement()?.pause(),
    autorizarSom: () => {
        const elemento = get().getAvisoElement()
        if (!elemento) return
        // Roda dentro do clique do usuario -> o play() e permitido e, alem de tocar o
        // aviso pendente, destrava o autoplay para os proximos toques da sessao.
        elemento
            .play?.()
            ?.then(() => set({ somBloqueado: false }))
            ?.catch(err => {
                console.warn("[AvisoPedido] autorizacao de som falhou:", err?.name || err)
                set({ somBloqueado: false })
            })
    },
    toggleAviso: () => {
        const elementoAudio = get().getAvisoElement()

        if (!elementoAudio) return

        elementoAudio.paused ? elementoAudio.play()?.catch(() => {}) : elementoAudio.pause()
    }
})

export default createAvisoPedidoStore
